<!-- Sports or racing: two switches, shared by Today's feed and History's piles.

     A ROW OF SHAPES, NOT A SEGMENT. Ionic's segment is a one-of-N control and
     these are two independent switches; and a second 44px bar under the first
     would spend a fifth of a 320px screen on something used twice a month. They
     are quiet when ON — carbon on paper, which is the state both screens are in
     almost always — and struck through when off, which is the pad's own mark
     for a thing that is not running.

     ONE COMPONENT BECAUSE IT IS ONE CONTROL. It was written on Today and copied
     nowhere; History asking the same question with the same two glyphs is the
     moment a second copy would start drifting — a different tap size here, a
     different stroke there, and the app has two ways of saying "hidden".

     IT OWNS NOTHING IT SHOWS. The filter comes in as a prop and the tap goes
     out as an event, so the two screens can remember their answers in two
     different places (`stores/feed-filter.js`, `stores/history-filter.js`) and
     this stays a control rather than a third opinion about what is showing. -->
<template>
  <div class="kinds" role="group" :aria-label="label">
    <button
      v-for="k in KINDS"
      :key="k.kind"
      type="button"
      class="kind"
      :class="{ 'kind--off': !filter[k.kind] }"
      :aria-pressed="filter[k.kind] ? 'true' : 'false'"
      @click="emit('toggle', k.kind)"
    >
      <IonIcon class="kind-glyph" :icon="k.icon" aria-hidden="true" />
      <span class="sr-only">{{ k.label }}</span>
    </button>
  </div>
</template>

<script setup>
import { IonIcon } from '@ionic/vue';
import { BALL, FLAG } from '../lib/sport-glyphs.js';

/**
 * The two toggles, in the order they print — the pad's own two shapes, read
 * from `sport-glyphs.js` rather than named again here, so a ticket's flag and
 * the switch that hides it can never be two different flags.
 */
const KINDS = [
  { kind: 'sports', icon: BALL, label: 'Show sports' },
  { kind: 'racing', icon: FLAG, label: 'Show racing' },
];

defineProps({
  /** `{ sports, racing }` — what is showing right now. */
  filter: { type: Object, required: true },
  /**
   * What the pair of them is FOR, for a screen reader arriving on the group.
   * Each button says what it does on its own; this says what the group decides.
   */
  label: { type: String, default: 'What to show' },
});

const emit = defineEmits(['toggle']);
</script>

<style scoped>
/* THE FILTER IS QUIET WHEN IT IS DOING NOTHING. Both kinds showing is the
   state these screens are in almost every morning, and a pair of filled ink
   blocks at the top of the pad would be the loudest thing on a page whose job
   is tickets.

   The track is the segment's, exactly: same hairline, same radius, same 3px of
   padding, so the two controls read as one bar rather than as a control and an
   afterthought. */
.kinds {
  display: flex;
  flex: 0 0 auto;
  gap: 2px;
  padding: 3px;
  border-radius: var(--r-action);
  background-color: var(--fill);
}

/* 44 wide and 48 tall: the recipe's `--tap`, and the height that makes this and
   the segment beside it one bar — the segment is 48 inside 3px of padding and a
   hairline, and a filter 10px shorter beside it reads as something stuck on
   afterwards. WCAG 2.2 SC 2.5.8 wants 24; this app has always wanted more. */
.kind {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 48px;
  padding: 0;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--carbon);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.kind:active {
  background-color: color-mix(in srgb, var(--carbon) 8%, transparent);
}

.kind:focus-visible {
  outline: 2px solid var(--carbon);
  outline-offset: -2px;
}

.kind-glyph {
  width: 20px;
  height: 20px;
}

/* STRUCK OUT, not greyed out. A faint glyph alone reads as disabled — as the
   app refusing — and this is the reader's own doing and undone by the same
   tap. The stroke is drawn across the glyph rather than under the button so it
   reads as a mark ON the thing, the way a scratched runner is struck through. */
.kind--off {
  color: var(--carbon-faint);
}

.kind--off::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 26px;
  height: 1.5px;
  background-color: var(--carbon-faint);
  transform: translate(-50%, -50%) rotate(-45deg);
}
</style>
